import React, { useState, useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import type { MultiLayoutGraphicBlockProps, Item } from './types'
import { withLayout } from '../../shared/Styles.js'
import Picture from '../../components/picture.js'
import Button from '../../components/button.js'
import { Heading } from '../../components/heading.js'
import { VideoModal } from '../VideoModal/index.js'
import { Grid, GridItem } from '../../components/grid.js'
import Title from '../Title/index.js'
import { cn, spaceToHyphen, getLocalizedPath } from '../../helpers/utils.js'
import SwiperBox from '../SwiperBox/index.js'
import { isVideo } from '../../shared/mimeType.js'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/tabs.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { gaTrack } from '../../shared/track.js'

const componentType = 'image'
const componentName = 'p1_banner'

const ItemBlock = ({
  data: item,
  configuration,
  jIndex,
  spanType,
  title: titleProp,
  onSecondaryButtonClick,
  onPrimaryButtonClick,
}: {
  data: Item
  configuration?: any
  jIndex?: number
  spanType?: string
  title?: string
  onSecondaryButtonClick?: (data: Item) => void
  onPrimaryButtonClick?: (data: Item) => void
}) => {
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' })

  const ref = useRef<HTMLDivElement>(null)

  const handleAspect = () => {
    if (spanType) {
      switch (spanType) {
        case 'full':
          return 'tablet:aspect-w-[704] tablet:aspect-h-[400] laptop:aspect-w-[896] laptop:aspect-h-[384] desktop:aspect-w-[1312] desktop:aspect-h-[512] lg-desktop:aspect-w-[1664] lg-desktop:aspect-h-[640]'
        case 'half':
          return 'tablet:aspect-w-[704] tablet:aspect-h-[480] laptop:aspect-w-[440] laptop:aspect-h-[384] desktop:aspect-w-[648] desktop:aspect-h-[512] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[640]'
        case 'one-third':
          return 'tablet:aspect-w-[296] tablet:aspect-h-[400] laptop:aspect-w-[288] laptop:aspect-h-[384] desktop:aspect-w-[427] desktop:aspect-h-[512] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[640]'
        default:
          return 'tablet:aspect-w-[296] tablet:aspect-h-[400] laptop:aspect-w-[288] laptop:aspect-h-[384] desktop:aspect-w-[427] desktop:aspect-h-[512] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[640]'
      }
    } else {
      switch (configuration?.num) {
        case 1:
          return 'tablet:aspect-w-[704] tablet:aspect-h-[400] laptop:aspect-w-[896] laptop:aspect-h-[384] desktop:aspect-w-[1312] desktop:aspect-h-[512] lg-desktop:aspect-w-[1664] lg-desktop:aspect-h-[640]'
        case 2:
          return 'tablet:aspect-w-[704] tablet:aspect-h-[480] laptop:aspect-w-[440] laptop:aspect-h-[384] desktop:aspect-w-[648] desktop:aspect-h-[512] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[640]'
        case 3:
          return 'tablet:aspect-w-[296] tablet:aspect-h-[400] laptop:aspect-w-[288] laptop:aspect-h-[384] desktop:aspect-w-[427] desktop:aspect-h-[512] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[640]'
        default:
          return 'tablet:aspect-w-[296] tablet:aspect-h-[400] laptop:aspect-w-[288] laptop:aspect-h-[384] desktop:aspect-w-[427] desktop:aspect-h-[512] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[640]'
      }
    }
  }

  useExposure(ref, {
    componentType: componentType,
    componentName: componentName,
    position: jIndex,
    componentTitle: item.title,
    componentDescription: item.description,
    navigation: configuration?.activeTab,
  })

  useEffect(() => {
    setIsMobile(mediaQuery)
  }, [mediaQuery])

  const {
    theme = 'light',
    title,
    description,
    imageUrl,
    primaryButton,
    secondaryButton,
    imageMobileUrl,
    blockLink,
    video,
    youtubeId,
    isYouTube,
    id,
  } = item
  const lgButtonSize = 'lg-desktop:px-7 lg-desktop:pb-[14px] lg-desktop:pt-[15px] lg-desktop:text-[16px]'

  return (
    <div
      className={cn(
        'item-wrapper cursor-pointer',
        'text-info-primary group relative box-border w-full overflow-hidden',
        handleAspect(),
        {
          'rounded-2xl': configuration?.shape === 'rounded',
          'aiui-dark': theme === 'dark',
          'h-[400px]': isMobile,
        },
        `text-info-primary`
      )}
      ref={ref}
    >
      {(blockLink || secondaryButton.link) && (
        <a
          className="absolute inset-0 z-10"
          href={trackUrlRef(
            getLocalizedPath(blockLink || secondaryButton.link || '', configuration?.locale || 'us'),
            `${componentType}_${componentName}`
          )}
          data-headless-type-name={`${componentType}#${componentName}`}
          data-headless-title-desc-button={`${title}#${description}`}
          data-headless-nav-position={`${configuration?.activeTab}#${jIndex}`}
          aria-hidden="true"
          tabIndex={-1}
        />
      )}
      <div className="absolute inset-0">
        {isVideo(imageUrl?.mimeType) ? (
          <video autoPlay muted playsInline loop className="tablet:block hidden size-full overflow-hidden object-cover">
            <source src={imageUrl?.url} type="video/mp4" />
          </video>
        ) : (
          <Picture
            source={imageUrl?.url}
            alt={imageUrl?.alt || ''}
            className="tablet:block hidden h-full overflow-hidden"
            imgClassName="h-full transition-all duration-300 group-hover:scale-105 object-cover"
            style={{ aspectRatio: `${imageUrl?.width}/${imageUrl?.height}` }}
          />
        )}
        {isVideo(imageMobileUrl?.mimeType) ? (
          <video autoPlay muted playsInline loop className="tablet:hidden block size-full overflow-hidden object-cover">
            <source src={imageMobileUrl?.url} type="video/mp4" />
          </video>
        ) : (
          <Picture
            source={imageMobileUrl?.url || imageUrl?.url}
            alt={imageMobileUrl?.alt || imageUrl?.alt || ''}
            className="tablet:hidden block h-full overflow-hidden"
            imgClassName="h-full transition-all duration-300 object-cover"
          />
        )}
        <div
          className={cn(
            'absolutestyle laptop:p-6 desktop:p-[32px] laptop:gap-4 desktop:gap-6 absolute inset-x-0 bottom-0 z-20 flex w-fit flex-col gap-6 p-4',
            {
              'fullstyle laptop:top-1/2 laptop:-translate-y-1/2 laptop:bottom-auto': item?.width === 'full',
            }
          )}
        >
          <div className="flex flex-col gap-1">
            <Heading size={3} as="h3" className="item-title" html={title} />
            <Heading
              as="h4"
              className="item-description desktop:text-[16px] lg-desktop:text-[18px] line-clamp-1 text-[14px]"
              html={description}
            />
          </div>
          <div className="lg-desktop:gap-3 flex gap-2">
            {secondaryButton && secondaryButton.text && (
              <Button
                aria-label={title ?? description}
                className={cn(lgButtonSize, 'link-left')}
                variant="secondary"
                as="a"
                data-track={`${id}-link-left`}
                onClick={() => {
                  onSecondaryButtonClick?.(item)
                }}
                href={trackUrlRef(
                  getLocalizedPath(secondaryButton.link || '', configuration?.locale || 'us'),
                  `${componentType}_${componentName}`
                )}
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${description}#${secondaryButton.text}`}
                data-headless-nav-position={`${configuration?.activeTab}#${jIndex}`}
              >
                {secondaryButton.text}
                <span className="sr-only">{title ?? description}</span>
              </Button>
            )}
            {primaryButton && primaryButton.text && (
              <Button
                aria-label={title ?? description}
                className={cn(lgButtonSize, 'link-right')}
                variant="primary"
                data-track={`${id}-link-right`}
                as="a"
                onClick={() => {
                  onPrimaryButtonClick?.(item)
                }}
                href={trackUrlRef(
                  getLocalizedPath(primaryButton.link || '', configuration?.locale || 'us'),
                  `${componentType}_${componentName}`
                )}
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${description}#${primaryButton.text}`}
                data-headless-nav-position={`${configuration?.activeTab}#${jIndex}`}
              >
                {primaryButton.text}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="laptop:bottom-6 laptop:right-6 absolute bottom-4 right-4 z-20">
          {(video?.url || youtubeId) && (
            <button
              onClick={() => {
                configuration?.onVideoPlayBtnClick?.(video?.url || youtubeId, isYouTube)
              }}
              className="laptop:size-12 flex size-8 items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-black/75"
            >
              <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7L0 14L0 0L12 7Z" fill="white" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const MultiLayoutGraphicBlock = React.forwardRef<HTMLDivElement, MultiLayoutGraphicBlockProps>((props, ref) => {
  const { data, className, onSecondaryButtonClick, onPrimaryButtonClick, ...rest } = props
  const { shape, sectionTitle, groupByTab = false, items = [], carousel } = data
  const [visible, setVisible] = useState<boolean>(false)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [youTubeId, setYouTubeId] = useState<string>('')
  const { locale = 'us' } = useAiuiContext()

  const getSpan = (width: 'full' | 'half' | 'one-third') => {
    switch (width) {
      case 'full':
        return 12
      case 'half':
        return 6
      case 'one-third':
        return 4
    }
  }

  const tabNames = items
    .map(item => item.tabName)
    .filter(Boolean)
    .filter((item, index, arr) => arr.indexOf(item) === index) as string[]

  const handleVideoPlayBtnClick = (url: string, isYouTube: boolean) => {
    setVisible(true)
    if (isYouTube) {
      setYouTubeId?.(url || '')
    } else {
      setVideoUrl?.(url || '')
    }
  }
  const handleTabNumber = (num: Number, flag?: boolean) => {
    switch (num) {
      case 1:
        return 1
      case 2:
        return 2
      default:
        return flag ? 2.3 : 3
    }
  }

  const tabItemsMaps = tabNames
    .map(tabName => ({
      tabName,
      items: items.filter(item => item.tabName === tabName),
    }))
    .reduce(
      (acc, cur) => {
        acc[cur.tabName] = cur.items
        return acc
      },
      {} as Record<string, (Item & { width?: 'full' | 'half' | 'one-third' })[]>
    )

  return (
    <section
      data-ui-component-id="MultiLayoutGraphicBlock"
      ref={ref}
      {...rest}
      className={cn('multiLayoutGraphicBlock', 'text-info-primary', className)}
    >
      {sectionTitle && <Title data={{ title: sectionTitle }} className="section-title" />}

      {groupByTab ? (
        <Tabs shape={shape} align="left" defaultValue={spaceToHyphen(tabNames[0]!)}>
          <TabsList>
            {tabNames.map((tabName, index) => (
              <TabsTrigger
                key={index}
                value={spaceToHyphen(tabName!)}
                onClick={() => {
                  gaTrack({
                    event: 'ga4Event',
                    event_name: 'component_click',
                    event_parameters: {
                      page_group: 'Home Page',
                      component_type: componentType,
                      component_name: componentName,
                      component_title: sectionTitle,
                      component_position: 1,
                      navigation: tabName,
                      button_name: tabName,
                    },
                  })
                }}
              >
                {tabName}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabNames.map((tabName, index) => (
            <TabsContent key={index} value={spaceToHyphen(tabName!)} className="desktop:mt-[36px] mt-[24px] w-full">
              <>
                <SwiperBox
                  className="!overflow-visible"
                  id={'MultiLayoutGraphicBlock1'}
                  data={{
                    list: tabItemsMaps?.[tabName] || [],
                    configuration: {
                      shape: shape,
                      isTab: groupByTab,
                      activeTab: tabName,
                      title: sectionTitle,
                      num: tabItemsMaps?.[tabName]?.length || 0,
                      locale,
                      onVideoPlayBtnClick: handleVideoPlayBtnClick,
                      onSecondaryButtonClick,
                      onPrimaryButtonClick,
                    },
                  }}
                  Slide={ItemBlock}
                  breakpoints={{
                    0: {
                      spaceBetween: 12,
                      freeMode: false,
                      slidesPerView: 1,
                    },
                    374: {
                      spaceBetween: 12,
                      freeMode: false,
                      slidesPerView: 1.2,
                    },
                    768: {
                      spaceBetween: 16,
                      freeMode: false,
                      slidesPerView: handleTabNumber(tabItemsMaps?.[tabName]?.length || 0, true),
                    },
                    1024: {
                      spaceBetween: 16,
                      freeMode: false,
                      slidesPerView: handleTabNumber(tabItemsMaps?.[tabName]?.length || 0),
                    },
                  }}
                />
                {carousel && carousel?.items.length > 0 ? (
                  <SwiperBox
                    className="!overflow-visible"
                    id={'MultiLayoutGraphicBlock2'}
                    data={{
                      list: carousel?.items || [],
                      configuration: {
                        shape: shape,
                        isTab: groupByTab,
                        locale,
                        onVideoPlayBtnClick: handleVideoPlayBtnClick,
                        title: sectionTitle,
                        onSecondaryButtonClick,
                        onPrimaryButtonClick,
                      },
                    }}
                    Slide={ItemBlock}
                    breakpoints={{
                      0: {
                        spaceBetween: 12,
                        freeMode: false,
                        slidesPerView: 1,
                      },
                      374: {
                        spaceBetween: 12,
                        freeMode: false,
                        slidesPerView: 1.2,
                      },
                      768: {
                        spaceBetween: 16,
                        freeMode: false,
                        slidesPerView: 2.3,
                      },
                      1024: {
                        spaceBetween: 16,
                        freeMode: false,
                        slidesPerView: 3.1,
                      },
                      1440: {
                        spaceBetween: 16,
                        freeMode: false,
                        slidesPerView: 4,
                      },
                    }}
                  />
                ) : null}
              </>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <>
          <Grid className="w-full">
            {items.map((item, index) => {
              return (
                <GridItem
                  key={`${item?.title || ''}${index}`}
                  span={getSpan(item.width ?? 'full')}
                  className="laptop:block hidden"
                >
                  <ItemBlock
                    data={item}
                    configuration={{
                      shape: shape,
                      locale,
                      onVideoPlayBtnClick: handleVideoPlayBtnClick,
                      title: sectionTitle,
                    }}
                    jIndex={index}
                    spanType={item.width}
                    onSecondaryButtonClick={onSecondaryButtonClick}
                    onPrimaryButtonClick={onPrimaryButtonClick}
                  />
                </GridItem>
              )
            })}
            {items.map((item, index) => (
              <GridItem key={`${item?.title || ''}${index}`} span={getSpan('full')} className="laptop:hidden block">
                <ItemBlock
                  data={item}
                  configuration={{
                    shape: shape,
                    locale,
                    onVideoPlayBtnClick: handleVideoPlayBtnClick,
                    title: sectionTitle,
                  }}
                  jIndex={index}
                  spanType={'full'}
                />
              </GridItem>
            ))}
          </Grid>
          {carousel && carousel?.items.length > 0 ? (
            <SwiperBox
              className="!overflow-visible"
              id={'MultiLayoutGraphicBlock3'}
              data={{
                list: carousel?.items || [],
                configuration: {
                  shape: shape,
                  locale,
                  onVideoPlayBtnClick: handleVideoPlayBtnClick,
                  title: sectionTitle,
                  onSecondaryButtonClick,
                  onPrimaryButtonClick,
                },
              }}
              Slide={ItemBlock}
              breakpoints={{
                0: {
                  spaceBetween: 12,
                  freeMode: false,
                  slidesPerView: 1,
                },
                374: {
                  spaceBetween: 12,
                  freeMode: false,
                  slidesPerView: 1.2,
                },
                768: {
                  spaceBetween: 16,
                  freeMode: false,
                  slidesPerView: 2.3,
                },
                1024: {
                  spaceBetween: 16,
                  freeMode: false,
                  slidesPerView: 3.1,
                },
                1440: {
                  spaceBetween: 16,
                  freeMode: false,
                  slidesPerView: 4,
                },
              }}
            />
          ) : null}
        </>
      )}
      {visible && (
        <VideoModal
          visible={visible}
          youTubeId={youTubeId}
          videoUrl={videoUrl}
          onCloseModal={() => setVisible(false)}
        />
      )}
    </section>
  )
})

MultiLayoutGraphicBlock.displayName = 'MultiLayoutGraphicBlock'

export default withLayout(MultiLayoutGraphicBlock)
export type { MultiLayoutGraphicBlockProps }
